import { HipaaPage } from '@/components/HipaaPage';
import { LoaderPage } from '@/components/LoaderPage';
import { TermsPage } from '@/components/TermsPage';
import EmailNotVerified from '@/pages/errors/EmailNotVerified';
import PasswordReset from '@/pages/errors/PasswordReset';
import Onboarding from '@/pages/onboarding/Onboarding';
import PostSso from '@/pages/post-sso/PostSso';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { AppRoutes } from '@/utils/types';
import { isMobile } from '@/utils/utils';
import { useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import Friends from './Friends/Friends';

export const Redirects = () => {
    const { user, isAuthenticated, isAnonymous, passwordReset, loginWithRedirect } = useAuth();
    const location = useLocation();
    const hipaaApprovalEnabled = useFlag('should-approve-hipaa', false);
    const termsConditionsStartTimestamp = useFlag('terms-conditions-start-timestamp', 0);
    const [searchParams] = useSearchParams();
    const { logout } = useAuth();
    const { userSettings, loading: userSettingsLoading, shouldOnBoardingDialogBeOpen, updateUserSettings } = useUserSettings();
    const [isHipaaAccepted, setHipaaAccepted] = useState(false);
    const [isHipaaCancelled, setHipaaCancelled] = useState(false);
    const [isTermsAccepted, setTermsAccepted] = useState(false);
    const ssoError = searchParams.get('error_description');
    const isOnMobile = isMobile();
    const isOnboarding =
        !isOnMobile &&
        shouldOnBoardingDialogBeOpen &&
        user?.email_verified &&
        !userSettingsLoading &&
        isAuthenticated &&
        !isAnonymous &&
        !userSettings?.features?.onBoarding?.finished;
    const isPostSsoPath = location.pathname.endsWith(AppRoutes.PostSso);
    const isPostSso = isPostSsoPath || ssoError;
    const isFriends = location.pathname.startsWith(AppRoutes.Friends);
    const isExistingUserOnBoarding =
        userSettings?.features?.onBoarding?.answers?.state?.onboard_class &&
        !userSettings?.features?.onBoarding?.answers?.state?.onboard_new_class;

    const userCreatedTimestamp = new Date(user?.createdAt || Date.now()).getTime();
    const shouldShowTerms =
        isAuthenticated &&
        user?.email_verified &&
        !isAnonymous &&
        !userSettingsLoading &&
        user?.createdAt &&
        termsConditionsStartTimestamp !== 0 &&
        userCreatedTimestamp >= termsConditionsStartTimestamp &&
        !userSettings?.features?.termsAccepted?.accepted;

    const handleTermsAccepted = async () => {
        const currentFeatures = userSettings?.features || {};
        await updateUserSettings({
            features: {
                ...currentFeatures,
                termsAccepted: {
                    accepted: true,
                    acceptedAt: new Date().toISOString(),
                },
            },
        });
        setTermsAccepted(true);
    };

    if (hipaaApprovalEnabled) {
        if (isHipaaCancelled) {
            logout({ returnTo: window.location.origin });
            return null;
        } else if (!isHipaaAccepted) {
            return <HipaaPage setAccepted={setHipaaAccepted} setCanceled={setHipaaCancelled} />;
        }
    }

    if (shouldShowTerms && !isTermsAccepted) {
        return <TermsPage setAccepted={handleTermsAccepted} />;
    }

    if (isAuthenticated && userSettingsLoading) {
        return <LoaderPage />;
    }

    if (user && !user.email_verified) {
        return <EmailNotVerified />;
    }

    if (isFriends) {
        if (!isAuthenticated) {
            loginWithRedirect({ appState: { returnTo: window.location.href } });
            return null;
        } else {
            return <Friends />;
        }
    }

    if (isOnboarding) {
        return <Onboarding isExistingUser={isExistingUserOnBoarding} />;
    }

    if (isPostSso) {
        return <PostSso redirectUri={isPostSsoPath ? undefined : window.location.href} />;
    }

    if (passwordReset) {
        return <PasswordReset />;
    }

    return <Outlet />;
};
